import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  cloudConfigured,
  isSignedIn,
  requestOtp,
  verifyOtp,
  getSignedInEmail,
  getApiOverride,
  setApiOverride,
  cloudApiUrl,
} from '../lib/cloud.js';

/**
 * Email-OTP sign-in gate. When a sync server is configured, opening Life OS
 * requires signing in with the owner's email + a one-time code (like Notion) —
 * not just the local passphrase. After verifying once, the JWT persists on this
 * device so subsequent opens go straight through; the local LockGate passphrase
 * still applies on top. With no server configured at all, the app stays purely
 * local and this gate steps aside.
 */
const OWNER_EMAIL = 'nikhilnagpure203@gmail.com';

const panelStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  background: 'var(--lc-surface, #0b0f0d)',
  padding: 24,
  zIndex: 1100,
};

const inputStyle: React.CSSProperties = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '10px 16px',
  font: 'inherit',
  textAlign: 'center',
  width: '100%',
  boxSizing: 'border-box',
};

const buttonStyle: React.CSSProperties = {
  background: 'var(--lc-primary)',
  color: 'var(--lc-on-primary)',
  border: 'none',
  borderRadius: 'var(--lc-radius-full)',
  padding: '10px 16px',
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  width: '100%',
};

export function AuthGate({ children }: { children: ReactNode }) {
  const [signedIn, setSignedIn] = useState(isSignedIn());
  const [phase, setPhase] = useState<'email' | 'code'>('email');
  const [email] = useState(getSignedInEmail() ?? OWNER_EMAIL);
  const [server, setServer] = useState(getApiOverride() || cloudApiUrl() || '');
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  // Purely local (no server anywhere) — no online sign-in to enforce.
  if (!cloudConfigured() && !server.trim()) return <>{children}</>;
  if (signedIn) return <>{children}</>;

  const sendCode = async () => {
    setBusy(true);
    setError('');
    setInfo('');
    if (server.trim()) setApiOverride(server.trim());
    const res = await requestOtp(email);
    setBusy(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? 'Could not send the code.');
      return;
    }
    setChallengeId(res.data.challengeId);
    setPhase('code');
    setInfo(`Sent a 6-digit code to ${email}. Check your inbox.`);
  };

  const verify = async () => {
    setBusy(true);
    setError('');
    const res = await verifyOtp(challengeId, code.trim(), email);
    setBusy(false);
    if (!res.ok) {
      setError(res.error ?? 'That code did not work. Try again.');
      return;
    }
    setSignedIn(true);
  };

  return (
    <div style={panelStyle}>
      <div style={{ width: 'min(380px, 92vw)', display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>Sign in to Life OS</div>
          <div style={{ color: 'var(--lc-on-surface-variant)', fontSize: 'var(--lc-text-sm)', marginTop: 4 }}>
            {phase === 'email'
              ? 'Verify it’s you with a one-time code sent to your email.'
              : `Enter the code we emailed to ${email}.`}
          </div>
        </div>

        {phase === 'email' ? (
          <>
            <input style={{ ...inputStyle, opacity: 0.85 }} value={email} readOnly aria-label="Owner email" />
            <input
              style={inputStyle}
              value={server}
              onChange={(e) => setServer(e.target.value)}
              placeholder="Sync server URL"
              aria-label="Sync server URL"
            />
            <button type="button" style={buttonStyle} disabled={busy} onClick={() => void sendCode()}>
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          </>
        ) : (
          <>
            <input
              style={inputStyle}
              value={code}
              autoFocus
              inputMode="numeric"
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void verify(); }}
              placeholder="6-digit code"
              aria-label="One-time code"
            />
            <button type="button" style={buttonStyle} disabled={busy} onClick={() => void verify()}>
              {busy ? 'Verifying…' : 'Verify & open'}
            </button>
            <button
              type="button"
              style={{ ...buttonStyle, background: 'transparent', color: 'var(--lc-on-surface-variant)' }}
              onClick={() => { setPhase('email'); setCode(''); setError(''); setInfo(''); }}
            >
              Use a different email / resend
            </button>
          </>
        )}

        {info ? <div style={{ color: 'var(--lc-primary)', fontSize: 'var(--lc-text-sm)' }}>{info}</div> : null}
        {error ? <div style={{ color: 'var(--lc-error, #e06)', fontSize: 'var(--lc-text-sm)' }}>{error}</div> : null}
      </div>
    </div>
  );
}
